import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Check, PenTool, X, Calendar, Users2, IndianRupee, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

/**
 * ContractSign — public, unauthenticated page rendered at /contracts/:token.
 *
 * Loads the contract via `GET /api/public/contracts/{token}` and — if
 * unsigned — presents a signature canvas + name input + Sign button.
 * On successful sign the page re-renders in a locked, receipt-style view
 * with the signature image and timestamp.
 */
export default function ContractSign() {
  const { token } = useParams();
  const backend = process.env.REACT_APP_BACKEND_URL;

  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState("");
  const [signerName, setSignerName] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${backend}/api/public/contracts/${token}`);
        if (!r.ok) throw new Error((await r.json()).detail || "Contract not found");
        const data = await r.json();
        setContract(data);
      } catch (e) {
        setError(e.message || "Could not load contract");
      } finally {
        setLoading(false);
      }
    })();
  }, [backend, token]);

  const submitSignature = async (dataUrl) => {
    if (!signerName.trim()) {
      toast.error("Please type your name before signing");
      return;
    }
    setSigning(true);
    try {
      const r = await fetch(`${backend}/api/public/contracts/${token}/sign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: signerName.trim(), signature_dataurl: dataUrl }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Sign failed");
      setContract(data);
      toast.success("Signed — thank you!");
    } catch (e) {
      toast.error(e.message || "Sign failed");
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <Loader2 className="w-6 h-6 animate-spin text-rose-700" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 p-6">
        <div className="max-w-md text-center">
          <X className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <h1 className="font-heading text-2xl font-semibold mb-2">Contract not found</h1>
          <p className="text-stone-600 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-stone-50 py-6 px-3 sm:px-6" data-testid="contract-sign-page">
      <div className="max-w-2xl mx-auto space-y-4">
        {/* Header brand */}
        <div className="text-center mb-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-semibold">Studio Contract</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-rose-900 mt-1">Kahani</h1>
          <div className="text-xs text-stone-500 italic">by Rakesh Janardan</div>
        </div>

        {/* Contract card */}
        <div className="bg-white rounded-2xl shadow-xl border border-stone-200 overflow-hidden">
          <div className="px-5 sm:px-8 py-6 border-b border-stone-100 bg-gradient-to-r from-rose-50/60 to-white">
            <div className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Client</div>
            <h2 className="font-heading text-2xl font-bold text-stone-900 mt-1" data-testid="contract-client">
              {contract.client_name}
            </h2>
            {contract.place && (
              <div className="text-sm text-stone-600 mt-0.5">📍 {contract.place}</div>
            )}
          </div>

          <div className="px-5 sm:px-8 py-5 space-y-4 text-sm">
            <Row icon={<Calendar className="w-4 h-4 text-rose-700" />} label="Event date(s)">
              {contract.event_dates?.length
                ? contract.event_dates.map((d) => <span key={d} className="inline-block mr-2 bg-stone-100 rounded px-2 py-0.5 text-xs font-mono" data-testid={`contract-date-${d}`}>{d}</span>)
                : <span className="text-stone-400 italic">TBD</span>}
            </Row>
            <Row icon={<Users2 className="w-4 h-4 text-rose-700" />} label="Services covered">
              {contract.roles?.length
                ? contract.roles.map((r) => <span key={r} className="inline-block mr-2 bg-rose-50 text-rose-800 border border-rose-200 rounded-full px-2 py-0.5 text-xs font-semibold">{r}</span>)
                : <span className="text-stone-400 italic">—</span>}
            </Row>
            <Row icon={<IndianRupee className="w-4 h-4 text-emerald-700" />} label="Booking amount">
              <span className="font-heading text-xl font-bold text-emerald-800 tabular-nums" data-testid="contract-total">
                {inr(contract.total_amount)}
              </span>
            </Row>

            {/* iter29 — Deliverables included (skipped when empty) */}
            {contract.deliverables && contract.deliverables.trim() && (
              <div className="pt-3 border-t border-stone-100" data-testid="contract-deliverables">
                <div className="text-[11px] uppercase tracking-wider text-emerald-800 font-bold mb-2 flex items-center gap-1">
                  🎁 Deliverables Included
                </div>
                <pre className="whitespace-pre-wrap text-xs text-stone-800 font-sans leading-relaxed bg-emerald-50/60 border border-emerald-200 rounded-md p-3">
                  {contract.deliverables}
                </pre>
              </div>
            )}

            {/* Terms */}
            <div className="pt-3 border-t border-stone-100">
              <div className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-2">Terms & Conditions</div>
              <pre className="whitespace-pre-wrap text-xs text-stone-700 font-sans leading-relaxed" data-testid="contract-terms">
                {contract.terms}
              </pre>
            </div>
          </div>

          {/* Signature area */}
          {contract.status === "signed" ? (
            <SignedReceipt contract={contract} inr={inr} />
          ) : (
            <SignatureCapture
              signerName={signerName}
              setSignerName={setSignerName}
              signing={signing}
              onSubmit={submitSignature}
            />
          )}
        </div>

        <div className="text-center text-[10px] text-stone-400 mt-6">
          © Kahani by Rakesh Janardan · Digitally signed and stored securely.
        </div>
      </div>
    </div>
  );
}

function Row({ icon, label, children }) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">{label}</div>
        <div className="text-sm text-stone-800 mt-0.5 flex flex-wrap gap-y-1">{children}</div>
      </div>
    </div>
  );
}

function SignedReceipt({ contract, inr }) {
  const when = contract.signed_at ? new Date(contract.signed_at).toLocaleString() : "";
  return (
    <div className="px-5 sm:px-8 py-6 bg-gradient-to-br from-emerald-50 to-white border-t-2 border-emerald-200" data-testid="contract-signed-receipt">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
          <Check className="w-6 h-6 text-white" strokeWidth={3} />
        </div>
        <div>
          <div className="font-heading text-lg font-bold text-emerald-900">Signed & Locked</div>
          <div className="text-xs text-stone-600" data-testid="contract-signer">
            by <span className="font-semibold">{contract.signed_by_name}</span> on {when}
          </div>
        </div>
      </div>
      {contract.signature_dataurl && (
        <div className="bg-white rounded-lg border border-emerald-200 p-3 inline-block">
          <img
            src={contract.signature_dataurl}
            alt="Signature"
            className="max-h-24"
            data-testid="contract-signature-image"
          />
        </div>
      )}
      <div className="text-[11px] text-stone-500 mt-3 flex items-center gap-1">
        <ShieldCheck className="w-3.5 h-3.5" />
        Total agreed: <span className="font-semibold text-stone-800">{inr(contract.total_amount)}</span>
      </div>
    </div>
  );
}

function SignatureCapture({ signerName, setSignerName, signing, onSubmit }) {
  const canvasRef = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });

  // Setup canvas — HiDPI-aware.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.strokeStyle = "#0c0a09";
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    // White background so the export PNG has a visible signature.
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const t = e.touches?.[0];
    const clientX = t ? t.clientX : e.clientX;
    const clientY = t ? t.clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastRef.current = getPos(e);
  };
  const draw = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const p = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
    if (isEmpty) setIsEmpty(false);
  };
  const end = () => { drawingRef.current = false; };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
    setIsEmpty(true);
  };

  const submit = () => {
    if (isEmpty) {
      toast.error("Please draw your signature above");
      return;
    }
    const dataUrl = canvasRef.current.toDataURL("image/png");
    onSubmit(dataUrl);
  };

  return (
    <div className="px-5 sm:px-8 py-6 border-t border-stone-100 bg-stone-50/50" data-testid="contract-sign-capture">
      <div className="mb-3">
        <label className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Your full name</label>
        <input
          type="text"
          value={signerName}
          onChange={(e) => setSignerName(e.target.value)}
          placeholder="e.g. Arti Sharma"
          className="w-full mt-1 border border-stone-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-rose-400 focus:ring-1 focus:ring-rose-200"
          data-testid="contract-sign-name"
        />
      </div>

      <div className="mb-2 flex items-center justify-between">
        <label className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold flex items-center gap-1">
          <PenTool className="w-3.5 h-3.5" /> Sign below
        </label>
        <button
          type="button"
          onClick={clear}
          className="text-[11px] text-stone-500 hover:text-red-700 underline"
          data-testid="contract-sign-clear"
        >
          Clear
        </button>
      </div>

      <canvas
        ref={canvasRef}
        className="w-full h-40 sm:h-48 border-2 border-dashed border-stone-300 rounded-lg bg-white touch-none cursor-crosshair"
        onMouseDown={start}
        onMouseMove={draw}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={draw}
        onTouchEnd={end}
        data-testid="contract-sign-canvas"
      />

      <button
        type="button"
        onClick={submit}
        disabled={signing}
        className="w-full mt-4 bg-rose-700 hover:bg-rose-800 text-white font-semibold py-3 rounded-md text-sm disabled:opacity-50 transition-colors"
        data-testid="contract-sign-submit"
      >
        {signing ? <><Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Signing…</> : "Agree & Sign"}
      </button>
      <p className="text-[10px] text-stone-500 text-center mt-2">
        By tapping Sign, you agree to the terms above. Your signature is legally binding.
      </p>
    </div>
  );
}
